/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import { fromApiSkuDetails } from '@/adaptors';
import { type IItem } from '@/components/AZCustomComponent/BreadcrumbSTARC/utils/isBreadcrumbModel';
import { pageTypes } from '@/constants/page';
import { useBrandProductData } from '@/features/brands/api/getProductBrandData';
import type { CategoryData, CategoryDataAces } from '@/features/category';
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { useMappedCategoryData } from '@/features/category/api/mapCategoryData';
import { useFeatureFlag } from '@/features/globalConfig';
import { type ProductSkuDetails } from '@/features/product';
import {
  useProductSkuDeals,
  useProductSkuDealsBatches,
} from '@/features/product/api/getProductDeals';
import {
  useProductSkuDetails,
  useProductSkuDetailsBatches,
} from '@/features/product/api/getProductSkuDetails';
import { usePageType } from '@/features/category/api/getPageType';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useEffect } from 'react';
import { shelfPageType } from '../constants';
import type {
  ProductFinder,
  ShelfHead,
  ShelfLeftNav,
  ShelfPageBodyComplete,
  ShelfPageBodyItemGeneric,
  ShelfPart,
  ShelfResultsBottomItemTwo,
  SortingOption,
} from '@/types/legacy';
import type { MappedBrandShelfResults, MappedBrandShelfResultsAces } from '@/features/brands';
import { generateNewProductRecordsForCitrus } from '../utils/generateNewProductRecords';
import { getShelfBodyParts } from '../utils/getShelfBodyparts';
import { getSkuIdsForPage } from '../utils/getSkuIdsForPage';
import { normalizeShelfResponse } from '../utils/normalizeShelfResponse';
import { shopByPageTypeInfo } from '../utils/shopByPageTypeInfo';
import { FACETED_URL, FACETED_URL_V2 } from '@/utils/validatorRegex';
import { type MappedSearchProductData } from '@/features/search';
import { useSearchProductData } from '@/features/search/api/getSearchProductData';
import { useCitrusShelfCarouselData } from '@/features/citrus/api/getCitrusShelfCarouselData';
import { useDeviceType } from '@/utils/useDeviceType';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { countryCodes } from '@/constants/locale';
import { useCatalogConfigs } from '@/features/common/api/getCatalogConfig';
import { useHeaderData } from '@/features/header/api/getHeader';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import type { UseProductListDataResult } from '../interface';
import { useCitrusSearchCarouselData } from '@/features/citrus/api/getCitrusSearchCarouselData';
import { getSkuIdsForSearchPage } from '../utils/getSkuIdsForSearchPage';
import type { FilterDataType } from '@/types/filterTypes';
import { useSearchSkuRecordData } from '@/features/search/api/getSearchRecordData';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { getDefaultSortingOptions } from '../utils/getDefaultSortingOptions';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useACESBrandProductData } from '@/features/brands/api/getAcesProductBrandData';
import { type AcesSearchSkuRecordResponse } from '../../search/interface';
import type { SkuDealsFromAPI } from '@/types/availability';
import { useMerchShelfResults } from '@/features/category/api/getMerchShelfResults';
import { useLabels } from '@/hooks/useLabels';
import { brandsPrefix } from '@/features/brands/constants/brandsPrefix';

type TransformedPageData = {
  brandName: string | undefined;
  makeModelYearPageType: 'ShopByMake' | 'ShopByModel' | 'ShopByYear' | undefined | null;
  makeorModelName?: string | undefined;
  catLabel: string | undefined;
  productId: string | undefined | null;
  shelfHead: ShelfHead[];
  shelfLeftNav: ShelfLeftNav | undefined;
  shelfPageBody: ShelfPageBodyComplete;
  shelfResultHeader: ShelfPageBodyItemGeneric[] | [];
  shelfResultBottom:
    | [ShelfPageBodyItemGeneric, ShelfResultsBottomItemTwo]
    | Array<{ '@type': string }>;
  sortingOptions: {
    options: SortingOption[];
    quickFilter: boolean;
  } | null;
  productFinderData: ProductFinder | null;
  productFilterData:
    | null
    | undefined
    | {
        mobile: boolean;
        tablet: boolean;
        desktop: boolean;
        '@type': string;
        contents: [
          {
            '@type': string;
            refinementList: Array<{ label: string }>;
          },
        ];
      };
  productRecords: ShelfPart[];
  isBrandPremiumPage?: boolean;
};

type BatchedDetailsSelector = {
  updatedShelfParts: ShelfPart[] | undefined;
  shelfBreadcrumb: IItem[];
};

const pageDataSelector = ({
  pageData,
  pathname,
  acesEnabled,
  isMerchShelfPage,
}: {
  pageData: CategoryData | CategoryDataAces | undefined;
  pathname: string;
  acesEnabled: boolean;
  isMerchShelfPage: boolean;
}): TransformedPageData => {
  const {
    brandName,
    catLabel,
    productId,
    shelfLeftNav,
    shelfPageBody,
    shelfResultBottom,
    shelfResultHeader,
    shelfHead,
  } = normalizeShelfResponse({
    data: pageData,
    pathname: pathname,
    acesEnabled,
    isMerchShelfPage,
  });
  const { resultsList, sortingOptions, productFinderData, productFilterData } =
    getShelfBodyParts(shelfPageBody);
  const productRecords = resultsList?.records ?? [];

  const makeModelYearPageType =
    pageData && 'makeModelYearPageType' in pageData ? pageData.makeModelYearPageType : null;

  return {
    brandName,
    makeModelYearPageType,
    catLabel,
    productId,
    shelfHead,
    shelfLeftNav,
    shelfPageBody,
    shelfResultHeader,
    shelfResultBottom,
    sortingOptions,
    productFinderData,
    productFilterData,
    productRecords,
  };
};

const skuDetailsSelector = ({
  allProductSkuDetails,
  allProductSkuDealsData,
  newProductRecords,
  pageData,
  query,
  shouldIncludeSponsoredProducts,
  acesEnabled,
  isNewDealsApiEnabled,
  labels,
  locale,
}: {
  allProductSkuDetails: Record<string, ProductSkuDetails> | undefined;
  allProductSkuDealsData: Record<string, SkuDealsFromAPI> | undefined;
  newProductRecords: ShelfPart[] | undefined;
  pageData: CategoryData | CategoryDataAces | undefined;
  query: {
    brandName?: string;
    params?: { minPrice: string; maxPrice: string } | undefined;
    seoUrlPath?: string[];
  };
  shouldIncludeSponsoredProducts: boolean;
  acesEnabled: boolean;
  isNewDealsApiEnabled: boolean;
  labels: Record<string, string>;
  locale: Locale;
}): BatchedDetailsSelector => {
  const updatedShelfParts: ShelfPart[] = [];

  for (const productRecord of newProductRecords ?? []) {
    const matchingSkuDetail = allProductSkuDetails?.[productRecord.skuNumber];
    const matchingSkuDeals = allProductSkuDealsData?.[productRecord.skuNumber];

    if (matchingSkuDetail && matchingSkuDeals && isNewDealsApiEnabled) {
      matchingSkuDetail.skuPricingAndAvailability['dealAvailable'] =
        matchingSkuDeals.skuDealsInfo.dealAvailable;

      matchingSkuDetail.skuPricingAndAvailability['dealConfigurableLabel'] =
        matchingSkuDeals.skuDealsInfo.dealConfigurableLabel;

      matchingSkuDetail.skuPricingAndAvailability['dealsAvailableQty'] =
        matchingSkuDeals.skuDealsInfo.dealsAvailableQty;

      matchingSkuDetail.skuPricingAndAvailability['discountedPrice'] =
        matchingSkuDeals.skuDealsInfo.discountedPrice;

      matchingSkuDetail.skuPricingAndAvailability['rebateUrl'] =
        matchingSkuDeals.skuRebatesInfo?.rebateUrl;

      matchingSkuDetail.skuPricingAndAvailability['rebateShortDesc'] =
        matchingSkuDeals.skuRebatesInfo?.rebateShortDesc;

      matchingSkuDetail.skuPricingAndAvailability['rebatesAvailable'] =
        matchingSkuDeals.skuRebatesInfo?.rebatesAvailable;
    }

    let product = productRecord;

    if (matchingSkuDetail) {
      const productSkuDetails = fromApiSkuDetails(matchingSkuDetail);
      product = { ...productSkuDetails, ...productRecord };
      if (acesEnabled) {
        product.warranty = productSkuDetails.warranty;
        product.warrantyText = productSkuDetails.warranty;
      }
    }

    if (
      shouldIncludeSponsoredProducts ||
      (!product.isSponsoredCard && !product.sponsoredProductFlag)
    ) {
      updatedShelfParts.push(product);
    }
  }

  // TODO backend service should change "displayName" to "label" to align with STARC
  const shelfBreadcrumb =
    pageData != null && 'breadCrumbsList' in pageData
      ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        pageData.breadCrumbsList?.slice(0).map((crumb) => ({
          ...crumb,
          label: crumb.displayName,
        }))
      : [];

  if (query.brandName) {
    shelfBreadcrumb.push({
      label: labels.label_navigation_shopAllBrands,
      url: `/${brandsPrefix[locale]}`,
    });
  }

  return { updatedShelfParts, shelfBreadcrumb };
};

const shelfDataSelector = ({
  locale,
  pageData,
  transformedPageData,
  shelfParts,
  shelfBreadcrumb,
  shelfResultHasAppItems,
  shouldUseAcesSortingOptions,
}: {
  locale: Locale;
  pageData: CategoryData | CategoryDataAces | undefined;
  transformedPageData: TransformedPageData;
  shelfParts: ShelfPart[];
  shelfBreadcrumb: IItem[];
  shelfResultHasAppItems: boolean;
  isSuccess?: boolean;
  shouldUseAcesSortingOptions: boolean;
}) => {
  const {
    brandName,
    makeModelYearPageType,
    productFilterData,
    productFinderData,
    catLabel,
    productId,
    shelfHead,
    shelfLeftNav,
    shelfPageBody,
    shelfResultHeader,
    shelfResultBottom,
    sortingOptions,
  } = transformedPageData;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const contents = pageData?.contents?.contents[0];

  const topContentSeoActive =
    pageData && 'topContentSeoActive' in pageData ? pageData.topContentSeoActive : undefined;

  const shopByUrlSuffix =
    pageData && 'shopByUrlSuffix' in pageData ? pageData.shopByUrlSuffix : undefined;

  const seoTopCopy =
    pageData && 'seoTopCopy' in pageData && topContentSeoActive ? pageData.seoTopCopy : null;

  const seoBottomCopy = pageData && 'seoBottomCopy' in pageData ? pageData.seoBottomCopy : '';

  const seoCopyBlock =
    pageData && 'seoCopyBlock' in pageData && seoTopCopy === null
      ? pageData.seoCopyBlock
      : seoBottomCopy;

  const shelfSeoCopyBlock = topContentSeoActive ? seoBottomCopy : seoCopyBlock;

  const shortDescription =
    pageData && 'shortDescription' in pageData ? pageData.shortDescription : null;

  const shelfProductListData = {
    brandName,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    isBrandPremiumPage: pageData?.contents?.contents[0]?.isBrandPremiumPage,
    description:
      pageData && 'description' in pageData && pageData.description ? pageData.description : '',
    shelfResultHasAppItems,
    hierarchyLocation:
      pageData && 'hierarchyLocation' in pageData && pageData.hierarchyLocation
        ? pageData.hierarchyLocation
        : '',
    makeModelYearPageType,
    productFilterData: !makeModelYearPageType ? productFilterData : null,
    productFinderData: !makeModelYearPageType ? productFinderData : null,
    productId,
    seoCopyBlock: shelfSeoCopyBlock,
    shelfBreadcrumb,
    shelfCatLabel: catLabel,
    shelfHead,
    shelfHeading: catLabel,
    shelfLeftNav,
    shelfPageBody,
    shelfParts,
    shelfResultHeader,
    shelfResultBottom,
    shelfSeoMetaData: pageData && 'seoMetaData' in pageData ? pageData.seoMetaData : null,
    shelfTippyTop: contents && 'shelfTippyTop' in contents ? contents.shelfTippyTop : [],
    shopByUrlMake:
      pageData && 'shopByUrlMake' in pageData && pageData.shopByUrlMake
        ? pageData.shopByUrlMake
        : undefined,
    shopByUrlYear:
      pageData && 'shopByUrlYear' in pageData && pageData.shopByUrlYear
        ? pageData.shopByUrlYear
        : undefined,
    shopByUrlModel:
      pageData && 'shopByUrlModel' in pageData && pageData.shopByUrlModel
        ? pageData.shopByUrlModel
        : undefined,
    seoBottomCopy: shelfSeoCopyBlock,
    shortDescription: shortDescription,
    yearRange: pageData && 'yearRange' in pageData ? pageData.yearRange : undefined,
    makeNames: pageData && 'makeNames' in pageData ? pageData.makeNames : undefined,
    categoryMake: pageData && 'categoryMake' in pageData ? pageData.categoryMake : undefined,
    categoryModel: pageData && 'categoryModel' in pageData ? pageData.categoryModel : undefined,
    makeorModelName:
      pageData && 'makeorModelName' in pageData
        ? pageData.makeorModelName
        : transformedPageData.makeorModelName,
    shopByUrlSuffix,
    seoTopCopy,

    modelNames: pageData && 'modelNames' in pageData ? pageData.modelNames : undefined,
    catLabel: pageData && 'catLabel' in pageData ? pageData.catLabel : undefined,
    sortingOptions: sortingOptions?.options.length
      ? sortingOptions
      : getDefaultSortingOptions(shouldUseAcesSortingOptions, locale),
    summary: pageData && 'summary' in pageData && pageData.summary ? pageData.summary : '',
  };

  return shelfProductListData;
};

const shelfLeftNav = ({
  name,
  staticNavigation,
}: {
  name: string;
  staticNavigation?: FilterDataType[];
}) => {
  return [
    {
      '@type': 'string',
      dynamicNavigation: {},
      name: `${name} Navigation`,
      staticNavigation,
    },
    {},
    {},
  ] as ShelfLeftNav;
};

const searchDataSelector = (
  partTypeData: MappedSearchProductData,
  locale: Locale,
  shouldUseAcesSortingOptions: boolean,
): TransformedPageData => {
  // Trust the backend: use only what the search API returns
  const searchHasYear = !!partTypeData.makeYear;
  const searchHasMake = !!partTypeData.makeName;
  const searchHasModel = !!partTypeData.modelName;

  // Count how many fields the backend returned
  const searchFieldCount = [searchHasYear, searchHasMake, searchHasModel].filter(Boolean).length;

  // Partial vehicle = backend returned exactly 2 fields
  const isPartialVehicleSearch = searchFieldCount === 2;

  // Use whichever fields the backend returned
  const partialVehicleName = isPartialVehicleSearch
    ? [partTypeData.makeYear, partTypeData.makeName, partTypeData.modelName]
        .filter(Boolean)
        .join(' ')
    : undefined;

  return {
    brandName: undefined,
    makeModelYearPageType: undefined,
    makeorModelName: partialVehicleName ?? undefined,
    catLabel: '',
    productId: undefined,
    shelfHead: [],
    shelfLeftNav: shelfLeftNav({ name: 'Search', staticNavigation: partTypeData.staticNavigation }),
    shelfPageBody: [],
    shelfResultHeader: [],
    shelfResultBottom: [],
    productFinderData: null,
    productFilterData: null,
    sortingOptions: getDefaultSortingOptions(shouldUseAcesSortingOptions, locale),
    productRecords: partTypeData.records ?? [],
  };
};

const productDataBrandSelector = (
  data: MappedBrandShelfResults | MappedBrandShelfResultsAces,
  shouldUseAcesSortingOptions: boolean,
  locale: Locale,
): TransformedPageData => ({
  brandName: data.brandName,
  isBrandPremiumPage: false,
  catLabel: data.brandName,
  productRecords: (data.records ?? []) as ShelfPart[],
  makeModelYearPageType: undefined,
  productId: undefined,
  shelfLeftNav: shelfLeftNav({ name: 'Brand', staticNavigation: data.staticNavigation }),
  shelfResultBottom: [],
  shelfResultHeader: [],
  shelfPageBody: [],
  shelfHead: [],
  sortingOptions: getDefaultSortingOptions(shouldUseAcesSortingOptions, locale),
  productFinderData: null,
  productFilterData: null,
});

export const useProductListData = (): UseProductListDataResult => {
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';
  const citrusEnabled = useFeatureFlag('IS_CITRUS_ENABLED') === 'true';
  const citrusShelfSponsoredCardsEnabled =
    useFeatureFlag('IS_CITRUS_SHELF_SPONSORED_CARDS_ENABLED') === 'true';
  const citrusSearchResultsSponsoredCardsEnabled =
    useFeatureFlag('IS_CITRUS_SEARCH_RESULTS_SPONSORED_CARDS_ENABLED') === 'true';
  const engineOilFilterName = 'Engine Oil';
  const deviceType = useDeviceType();
  const labels = useLabels({ label_navigation_shopAllBrands: 'label_navigation_shopAllBrands' });

  const router = useLegacyRouter();
  const { query, asPath } = router;
  const isSearchPage = router.pathname.includes('/searchresult');
  const { filters, facet, searchText } = query;
  const { data: pageTypeData, isFetching: isPageTypeFetching } = usePageType();

  // hidden query parameter
  const loadMoreClicked = query.loadMore === 'true';

  const hasFilters =
    FACETED_URL.test(asPath.split('?')[0]) ||
    FACETED_URL_V2.test(asPath.split('?')[0]) ||
    !!filters ||
    !!facet;
  const locale = useLocale();
  const isBrandPage = isBrandShelfPage(router.asPath);
  const isMerchShelfPage = router.route === '/deals/[cmsPath]/[[...slug]]';
  const raEngineOilFilterOnlyCheck = () => {
    if (
      FACETED_URL_V2.test(asPath.split('?')[0]) ||
      facet?.includes('+') ||
      !facet?.includes(engineOilFilterName)
    ) {
      return false;
    }

    return true;
  };

  const hasEngineOilFilterSelectedOnly = raEngineOilFilterOnlyCheck();
  const { acesEnabled } = useAcesFlags();
  const hasCustomPrice = query.maxPrice || query.minPrice;
  const useBrandProductDataFunction = acesEnabled ? useACESBrandProductData : useBrandProductData;
  const {
    data: brandProductData,
    status: brandProductStatus,
    isFetching: brandProductIsFetching,
    pages: brandProductPages,
  } = useBrandProductDataFunction({
    enabled: isBrandPage,
  });

  const {
    data: searchProductData,
    status: searchProductQueryStatus,
    isFetching: searchProductIsFetching,
    pages: searchProductPages,
  } = useSearchProductData({
    enabled: pageTypeData?.pageType === pageTypes.search && query.partTypesView !== 'true',
  });
  const { data: searchSkuRecordData, status: searchSkuRecordQueryStatus } =
    useSearchSkuRecordData();
  const acesSearchSkuRecordData = searchSkuRecordData as AcesSearchSkuRecordResponse | undefined;

  const { data: categoryData, status: categoryQueryStatus } = useMappedCategoryData();

  const searchProductSkus =
    isSearchPage && !searchProductData.redirectUrl
      ? (searchProductData.records?.map((record) => record.skuNumber.toString()) ?? [])
      : [];

  const isShelf = pageTypeData?.pageType === shelfPageType.productShelf;

  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const { data: productShelfData, pages: productShelfPages } = useShelfResultsFunction();

  const { pages: merchShelfPages } = useMerchShelfResults();

  /* Analytics start Shelf-page SeoPage */
  useEffect(() => {
    if (isShelf) {
      shopByPageTypeInfo(pageTypeData.makeModelYearPath ?? '', []);
    }
  }, [pageTypeData?.pageType, pageTypeData?.makeModelYearPath, isShelf]);
  /* Analytics end */

  const pageData: CategoryData | CategoryDataAces | undefined = isBrandPage
    ? undefined
    : categoryData;
  const { data: headerData, isFetching: headerIsFetching } = useHeaderData();
  const isVehicleSelected = acesEnabled
    ? !!headerData?.vehicleMap?.vehicleId
    : !!headerData?.vehicleMap?.catalogVehicleId;
  const catalogConfigsEnabledForSearch =
    acesEnabled && Boolean(searchProductData) && Boolean(searchSkuRecordData);

  const catalogConfigsEnabledForShelf = acesEnabled && !!pageTypeData?.catalogId && !isBrandPage;

  const catalogConfigsEnabled = isSearchPage
    ? catalogConfigsEnabledForSearch
    : catalogConfigsEnabledForShelf;
  const acesProductShelfResults =
    productShelfData?.productShelfResults &&
    'partTerminologyId' in productShelfData.productShelfResults
      ? productShelfData.productShelfResults
      : undefined;

  const catalogConfigsItemId = isSearchPage
    ? acesSearchSkuRecordData?.searchResults?.skuRecord?.partGroupId
    : (pageTypeData?.catalogId ?? acesProductShelfResults?.partTerminologyId);

  const { data: catalogConfigsData, isFetching: catalogConfigsDataIsFetching } = useCatalogConfigs({
    itemId: catalogConfigsItemId ?? '',
    itemType: 'product',
    enabled: catalogConfigsEnabled,
  });

  const citrusShelfSPAsEnabled =
    citrusShelfSponsoredCardsEnabled && pageTypeData?.pageType === pageTypes.ProductShelf;
  const citrusSearchResultsSPAsEnabled = citrusSearchResultsSponsoredCardsEnabled && isSearchPage;
  const defaultCitrusChecks =
    deviceType !== 'bot' &&
    citrusEnabled &&
    (hasEngineOilFilterSelectedOnly || !hasFilters) &&
    !hasCustomPrice;

  let searchMatchesPartNumbers = false;
  if (searchText && typeof searchText === 'string') {
    const searchLower = searchText.toLowerCase().trim();
    searchMatchesPartNumbers = (searchProductData.records || []).some((record) => {
      const partNumber = record.partNumber?.toLowerCase() || '';
      const oemPartNumber = record.oemPartNumber?.toLowerCase() || '';
      const alternatePartNumber = record.alternatePartNumber?.toLowerCase() || '';

      return (
        partNumber === searchLower ||
        oemPartNumber === searchLower ||
        alternatePartNumber === searchLower
      );
    });
  }

  const frontEndShouldCallCitrus: boolean = Boolean(
    catalogConfigsEnabled &&
    catalogConfigsData &&
    catalogConfigsData.configs &&
    !isVehicleSelected &&
    catalogConfigsData.configs.IS_SPNSRD_ADVRTSMT_FL,
  );

  const shouldFetchShelfCitrusProducts =
    defaultCitrusChecks && frontEndShouldCallCitrus && citrusShelfSPAsEnabled;

  const catalogConfigsReady =
    catalogConfigsDataIsFetching === false && isPageTypeFetching === false;
  const citrusShelfConfigsReady = catalogConfigsReady;

  const { data: citrusShelfData, isSuccess: citrusShelfSuccess } = useCitrusShelfCarouselData({
    enabled:
      shouldFetchShelfCitrusProducts && headerIsFetching === false && citrusShelfConfigsReady,
  });

  const shouldFetchSearchPageCitrusProducts =
    defaultCitrusChecks &&
    frontEndShouldCallCitrus &&
    citrusSearchResultsSPAsEnabled &&
    !searchMatchesPartNumbers;

  const citrusSearchConfigsReady = catalogConfigsDataIsFetching === false;

  const { data: citrusSearchPageData, isSuccess: citrusSearchPageSuccess } =
    useCitrusSearchCarouselData({
      enabled:
        shouldFetchSearchPageCitrusProducts &&
        citrusSearchConfigsReady &&
        headerIsFetching === false &&
        searchProductQueryStatus === 'success' &&
        searchSkuRecordQueryStatus === 'success' &&
        !searchProductData.redirectUrl &&
        !searchSkuRecordData.redirectUrl,
    });

  const citrusData = isSearchPage ? citrusSearchPageData : citrusShelfData;

  let pagedSkuIds: string[][];
  let sponsoredSkus: string[];

  if (isBrandPage) {
    pagedSkuIds =
      brandProductPages?.map(
        (page) => page.brandShelfResults?.skuRecords?.map((sku) => sku.itemId) ?? [],
      ) ?? [];
    sponsoredSkus = [];
  } else if (isSearchPage) {
    ({ sponsoredSkus } = getSkuIdsForSearchPage(searchProductSkus, citrusSearchPageData));

    pagedSkuIds =
      searchProductPages?.map(
        (page) => page.searchResults?.skuRecords?.map((sku) => sku.itemId) ?? [],
      ) ?? [];
  } else if (acesEnabled && isMerchShelfPage) {
    pagedSkuIds =
      merchShelfPages?.map(
        (page) => page.merchShelfResults?.skuRecords?.map((sku) => sku.itemId) ?? [],
      ) ?? [];
    sponsoredSkus = [];
  } else {
    ({ sponsoredSkus } = getSkuIdsForPage(pageData, router.asPath, citrusShelfData));

    pagedSkuIds =
      productShelfPages?.map(
        (page) => page.productShelfResults?.skuRecords?.map((sku) => sku.itemId) ?? [],
      ) ?? [];
  }

  const isEnabled = Boolean(
    pagedSkuIds[0]?.length &&
    ((!brandProductIsFetching && brandProductStatus === 'success') ||
      (!searchProductIsFetching && searchProductQueryStatus === 'success') ||
      categoryQueryStatus === 'success'),
  );

  const isSponsoredEnabled = Boolean(
    sponsoredSkus.length &&
    ((!brandProductIsFetching && brandProductStatus === 'success') ||
      (!searchProductIsFetching && searchProductQueryStatus === 'success') ||
      categoryQueryStatus === 'success'),
  );

  const sponsoredSkuDetailsResult = useProductSkuDetails({
    skuIds: sponsoredSkus,
    enabled: isSponsoredEnabled,
  });

  const {
    combinedData: batchedProductSkuDetails,
    isPending: batchedProductSkuIsPending,
    isFetching: batchedProductSkuIsFetching,
    isError: batchedProductSkusIsError,
    rawQueryResults: batchedProductSkuRawQueryResults,
    pageInformation: batchedProductSkuPageInformation,
  } = useProductSkuDetailsBatches({
    skuIds: pagedSkuIds,
    enabled: isEnabled,
  });

  const allProductSkuDetails: Record<string, ProductSkuDetails> = {
    ...batchedProductSkuDetails,
  };

  // Adjust page indices for regular products (they start at index 1 since sponsored is at 0)
  const productSkuPageInformation: Record<string, number> = {};
  for (const [skuId, pageIndex] of Object.entries(batchedProductSkuPageInformation)) {
    productSkuPageInformation[skuId] = pageIndex + 1;
  }

  // Add sponsored SKU details to combined data (page index 0)
  if (sponsoredSkuDetailsResult.data) {
    for (const skuDetail of sponsoredSkuDetailsResult.data) {
      const skuId = skuDetail.skuPricingAndAvailability.skuId;
      allProductSkuDetails[skuId] = skuDetail;
      productSkuPageInformation[skuId] = 0;
    }
  }

  const productSkuIsPending = batchedProductSkuIsPending || sponsoredSkuDetailsResult.isPending;
  const productSkuIsFetching = batchedProductSkuIsFetching || sponsoredSkuDetailsResult.isFetching;
  const productSkusIsError = batchedProductSkusIsError || sponsoredSkuDetailsResult.isError;

  const productSkuRawQueryResults = [
    {
      isPending: sponsoredSkuDetailsResult.isPending,
      isFetching: sponsoredSkuDetailsResult.isFetching,
      isError: sponsoredSkuDetailsResult.isError,
      isSuccess: sponsoredSkuDetailsResult.isSuccess,
      status: sponsoredSkuDetailsResult.status,
      error: sponsoredSkuDetailsResult.error,
      skuIds: sponsoredSkus,
    },
    ...batchedProductSkuRawQueryResults,
  ];

  // Check if any batch of product SKU details is still fetching
  const additionalProductSkuDetailsIsFetching =
    pagedSkuIds.length > 1 && batchedProductSkuRawQueryResults.length > 0
      ? batchedProductSkuRawQueryResults.some((batch) => batch.isFetching)
      : false;

  let sponsoredSkuUniqueIds: Array<{ skuId: string; uniqueId?: number }> | undefined = undefined;
  let pagedSkuUniqueIds: Array<Array<{ skuId: string; uniqueId?: number }>> | undefined = undefined;

  if (acesEnabled) {
    const extractSkuUniqueIdsFromRecords = (
      records: Array<{ itemId: string; uniqueId?: number }>,
    ): Array<{ skuId: string; uniqueId?: number }> => {
      return records.map((record) => ({
        skuId: record.itemId,
        uniqueId: record.uniqueId,
      }));
    };

    // Extract uniqueId data for sponsored SKUs (citrus products don't have uniqueId, so only provide skuIds)
    sponsoredSkuUniqueIds = undefined;

    if (isBrandPage && brandProductPages) {
      pagedSkuUniqueIds = brandProductPages.map((page) =>
        extractSkuUniqueIdsFromRecords(page.brandShelfResults?.skuRecords ?? []),
      );
    } else if (isSearchPage && searchProductPages) {
      pagedSkuUniqueIds = searchProductPages.map((page) =>
        extractSkuUniqueIdsFromRecords(page.searchResults?.skuRecords ?? []),
      );
    } else if (isMerchShelfPage && merchShelfPages) {
      pagedSkuUniqueIds = merchShelfPages.map((page) =>
        extractSkuUniqueIdsFromRecords(page.merchShelfResults?.skuRecords ?? []),
      );
    } else if (productShelfPages) {
      pagedSkuUniqueIds = productShelfPages.map((page) =>
        extractSkuUniqueIdsFromRecords(page.productShelfResults?.skuRecords ?? []),
      );
    }
  }

  const sponsoredSkuDealsResult = useProductSkuDeals({
    skuIds: sponsoredSkus,
    skuUniqueIds: sponsoredSkuUniqueIds,
    enabled: isSponsoredEnabled && isNewDealsApiEnabled,
  });

  const { combinedData: batchedProductSkuDealsData } = useProductSkuDealsBatches({
    skuIds: pagedSkuIds,
    skuUniqueIds: pagedSkuUniqueIds,
    enabled: isEnabled && isNewDealsApiEnabled,
  });

  const allProductSkuDealsData: Record<string, SkuDealsFromAPI> = {
    ...batchedProductSkuDealsData,
  };

  // Add sponsored SKU deals to combined data
  if (sponsoredSkuDealsResult.data) {
    for (const skuDeal of sponsoredSkuDealsResult.data) {
      allProductSkuDealsData[skuDeal.skuId] = skuDeal;
    }
  }

  let status: UseProductListDataResult['status'];
  const isFetching = searchProductIsFetching || brandProductIsFetching;
  if (
    categoryQueryStatus === 'success' ||
    brandProductStatus === 'success' ||
    searchProductQueryStatus === 'success'
  ) {
    status = 'success';
  } else if (
    categoryQueryStatus === 'pending' ||
    searchProductQueryStatus === 'pending' ||
    brandProductStatus === 'pending'
  ) {
    status = 'pending';
  } else if (productSkusIsError) {
    status = 'error';
  } else {
    // After checking for 'success' and 'pending':
    // - searchProductQueryStatus and brandProductStatus are narrowed to 'error' (React Query has no 'idle')
    // - categoryQueryStatus can be 'error' | 'idle'
    status = categoryQueryStatus === 'error' ? 'error' : 'idle';
  }

  //TODO: Remove the !isShelf and !isBrandPage conditions when the product-shelves and brand-shelves endpoints are migrated to ACES
  const shouldUseAcesSortingOptions =
    locale === countryCodes.ptBr ? acesEnabled : acesEnabled && !isShelf;

  let transformedPageData: TransformedPageData;
  if (!pageData && !searchProductData.redirectUrl) {
    transformedPageData = searchDataSelector(
      searchProductData,
      locale,
      shouldUseAcesSortingOptions,
    );
  } else if (isBrandPage) {
    transformedPageData = productDataBrandSelector(
      brandProductData,
      shouldUseAcesSortingOptions,
      locale,
    );
  } else {
    transformedPageData = pageDataSelector({
      pageData,
      pathname: router.asPath,
      acesEnabled,
      isMerchShelfPage,
    });
  }
  if (isBrandPage) {
    const data = productDataBrandSelector(brandProductData, shouldUseAcesSortingOptions, locale);
    transformedPageData.productRecords = data.productRecords;
    transformedPageData.brandName = data.brandName;
    transformedPageData.isBrandPremiumPage = data.isBrandPremiumPage;
    transformedPageData.catLabel = data.catLabel;
    transformedPageData.shelfLeftNav = data.shelfLeftNav;
  }
  const newProductRecords =
    citrusEnabled &&
    (citrusShelfSPAsEnabled || citrusSearchResultsSPAsEnabled) &&
    (shouldFetchShelfCitrusProducts || shouldFetchSearchPageCitrusProducts)
      ? generateNewProductRecordsForCitrus({
          productRecords: transformedPageData.productRecords,
          sponsoredData: citrusData,
          vehicleId: headerData?.vehicleMap?.vehicleId,
        })
      : transformedPageData.productRecords;

  const shelfResultHasAppItems = newProductRecords.some(
    (product) => product.originalSystemCode === 0,
  );

  const shouldIncludeSponsoredProducts =
    (citrusSearchResultsSPAsEnabled || citrusShelfSPAsEnabled) &&
    (!frontEndShouldCallCitrus ||
      ((hasEngineOilFilterSelectedOnly || !hasFilters) && !hasCustomPrice));

  const { updatedShelfParts, shelfBreadcrumb } = skuDetailsSelector({
    allProductSkuDetails,
    allProductSkuDealsData,
    newProductRecords,
    pageData,
    query,
    shouldIncludeSponsoredProducts,
    acesEnabled,
    isNewDealsApiEnabled,
    labels,
    locale,
  });
  const shelfParts = updatedShelfParts ?? [];

  if (
    !frontEndShouldCallCitrus &&
    !citrusShelfSuccess &&
    !citrusSearchPageSuccess &&
    locale === countryCodes.us
  ) {
    shelfParts.forEach((part) => {
      part.isSponsoredCard = Boolean(part.sponsoredProductFlag);
    });
  }

  const shelfData = shelfDataSelector({
    locale,
    pageData,
    transformedPageData,
    shelfParts,
    shelfBreadcrumb,
    shelfResultHasAppItems,
    shouldUseAcesSortingOptions,
  });

  const data = status === 'success' ? shelfData : undefined;

  return {
    data,
    sponsoredSkus,
    pagedSkuIds,
    status,
    isSuccess: status === 'success',
    isPending: status === 'pending',
    isError: status === 'error',
    isIdle: status === 'idle',
    isFetching,
    loadMoreClicked,
    productSkuIsFetching,
    productSkuIsPending,
    additionalProductSkuDetailsIsFetching,
    productSkuRawQueryResults,
    productSkuPageInformation,
  };
};
